import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { AccountLayout, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import {
    AccountInfo,
    Connection,
    GetProgramAccountsFilter,
    KeyedAccountInfo,
    ParsedAccountData,
    PublicKey
} from "@solana/web3.js";
import {
    MARKETS_API,
    RPC_URL,
    WRAPPED_SOL_MINT,
    filterTruthy,
    lamportsToUiAmount,
    stringLamportsToUiAmountDecimal
} from "@bridgesplit/utils";
import axios from "axios";

const TOKEN_2022_PROGRAM_ID = new PublicKey("TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb");

// Seeker Genesis Token (SGT) constants from Solana Mobile
// https://docs.solanamobile.com/marketing/engaging-seeker-users#transferability
export const SGT_MINT_AUTHORITY = "GT2zuHVaZQYZSyQMgJPLzvkmyztfyXg2NJunqFp4p3A4";
export const SGT_METADATA_ADDRESS = "GT22s89nU4iWFkNXj1Bw6uYhJJWDRPpShHt4Bk8f99Te";
export const SGT_GROUP_MINT_ADDRESS = "GT22s89nU4iWFkNXj1Bw6uYhJJWDRPpShHt4Bk8f99Te";

export interface SGTVerificationResult {
    isValid: boolean;
    mintAddress: string;
    tokenAccountAddress: string;
    error?: string;
}

interface MintAccountExtension {
    extension: string;
    state: {
        authority?: string;
        metadataAddress?: string;
        group?: string;
        mint?: string;
        memberNumber?: number;
        delegate?: string;
        closeAuthority?: string;
        memberAddress?: string;
    };
}

interface ParsedMintInfo {
    mintAuthority: string | null;
    supply: string;
    decimals: number;
    isInitialized: boolean;
    freezeAuthority: string | null;
    extensions?: MintAccountExtension[];
}
const USER_TOKENS_ROUTE = "/user/tokens";
const USER_WALLET_HEADER = "User-Wallet";

type HttpToken = {
    mint: string;
    tokenAccountAddress: string;
    tokenAmount: {
        amount: string;
        decimals: number;
        uiAmount: number;
    };
};

type UserTokensHttpResponse = {
    tokens: HttpToken[];
};

export type TokenAmount = {
    amount: string;
    decimals: number;
    uiAmount: number | null;
};

export type UserTokenAccount = {
    address: string;
    mint: string;
    tokenAmount: TokenAmount;
};

type RpcWebSocketEventHandler = (...args: unknown[]) => void;

type InternalRpcWebSocket = {
    connect?: () => void;
    on?: (event: string, handler: RpcWebSocketEventHandler) => void;
    off?: (event: string, handler: RpcWebSocketEventHandler) => void;
    removeListener?: (event: string, handler: RpcWebSocketEventHandler) => void;
    setAutoReconnect?: (value: boolean) => void;
    setMaxReconnects?: (value: number) => void;
};

type ConnectionWithInternalWebSocket = Connection & {
    _rpcWebSocket?: InternalRpcWebSocket;
    _rpcWebSocketConnected?: boolean;
};

const RPC_WEBSOCKET_RETRY_BASE_MS = 1000;
const RPC_WEBSOCKET_RETRY_MAX_MS = 30000;

function disableWebsocketRetries(connection: Connection) {
    const internalConnection = connection as ConnectionWithInternalWebSocket;
    const rpcWebSocket = internalConnection._rpcWebSocket;

    rpcWebSocket?.setAutoReconnect?.(false);
    rpcWebSocket?.setMaxReconnects?.(0);
}

function removeRpcWebSocketListener(
    rpcWebSocket: InternalRpcWebSocket | undefined,
    event: string,
    handler: RpcWebSocketEventHandler
) {
    rpcWebSocket?.off?.(event, handler);
    rpcWebSocket?.removeListener?.(event, handler);
}

function getRpcWebsocketReconnectDelay(attempt: number) {
    return Math.min(RPC_WEBSOCKET_RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1), RPC_WEBSOCKET_RETRY_MAX_MS);
}

function attachRpcWebsocketReconnect(
    connection: Connection,
    { onReconnect }: { onReconnect?: () => Promise<void> | void } = {}
) {
    const internalConnection = connection as ConnectionWithInternalWebSocket;
    const rpcWebSocket = internalConnection._rpcWebSocket;

    if (!rpcWebSocket?.on) return () => undefined;

    let reconnectAttempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    let isReconnectSyncInFlight = false;
    let shouldResyncOnNextOpen = false;

    const clearReconnectTimer = () => {
        if (!reconnectTimer) return;
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
    };

    const runReconnectSyncIfNeeded = () => {
        if (
            disposed ||
            isReconnectSyncInFlight ||
            !shouldResyncOnNextOpen ||
            !internalConnection._rpcWebSocketConnected
        ) {
            return;
        }

        shouldResyncOnNextOpen = false;
        isReconnectSyncInFlight = true;

        Promise.resolve(onReconnect?.())
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error("Failed to refresh token balances after RPC websocket reconnect", error);
            })
            .finally(() => {
                isReconnectSyncInFlight = false;
                runReconnectSyncIfNeeded();
            });
    };

    const scheduleReconnect = () => {
        if (disposed || reconnectTimer || internalConnection._rpcWebSocketConnected) return;

        reconnectAttempt += 1;
        const reconnectDelay = getRpcWebsocketReconnectDelay(reconnectAttempt);

        reconnectTimer = setTimeout(() => {
            reconnectTimer = undefined;

            if (disposed || internalConnection._rpcWebSocketConnected) return;

            try {
                rpcWebSocket.connect?.();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Failed to reconnect RPC websocket", error);
                scheduleReconnect();
            }
        }, reconnectDelay);
    };

    const handleOpen: RpcWebSocketEventHandler = () => {
        clearReconnectTimer();
        reconnectAttempt = 0;
        runReconnectSyncIfNeeded();
    };

    const handleClose: RpcWebSocketEventHandler = (code) => {
        if (disposed || code === 1000) return;

        shouldResyncOnNextOpen = true;
        scheduleReconnect();
    };

    rpcWebSocket.on("open", handleOpen);
    rpcWebSocket.on("close", handleClose);

    return () => {
        disposed = true;
        clearReconnectTimer();
        removeRpcWebSocketListener(rpcWebSocket, "open", handleOpen);
        removeRpcWebSocketListener(rpcWebSocket, "close", handleClose);
    };
}

function getTokenUiAmountWithFallback(
    amount: string,
    decimals: number,
    uiAmount: number | null | undefined
): number | null {
    const calculatedUiAmountString = stringLamportsToUiAmountDecimal(amount, decimals);
    const parsedUiAmount = Number.parseFloat(calculatedUiAmountString);

    return Number.isFinite(parsedUiAmount) ? parsedUiAmount : uiAmount ?? null;
}

function mapHttpTokenToUserTokenAccount(token: HttpToken): UserTokenAccount {
    const uiAmount = getTokenUiAmountWithFallback(
        token.tokenAmount.amount,
        token.tokenAmount.decimals,
        token.tokenAmount.uiAmount
    );
    return {
        address: token.tokenAccountAddress,
        mint: token.mint,
        tokenAmount: {
            amount: token.tokenAmount.amount,
            decimals: token.tokenAmount.decimals,
            uiAmount
        }
    };
}

export const userTokensRestApi = createApi({
    reducerPath: "userTokensRestApi",
    baseQuery: fetchBaseQuery({ baseUrl: MARKETS_API }),
    endpoints: (builder) => ({
        userTokens: builder.query<UserTokenAccount[], string>({
            query: (walletAddress) => ({
                url: USER_TOKENS_ROUTE,
                method: "GET",
                headers: {
                    [USER_WALLET_HEADER]: walletAddress
                }
            }),
            transformResponse: (response: UserTokensHttpResponse) =>
                response.tokens.map((token) => mapHttpTokenToUserTokenAccount(token))
        })
    })
});

function createRpcConnection() {
    const connection = new Connection(RPC_URL, "processed");
    disableWebsocketRetries(connection);
    return connection;
}

function createQueryError(error: unknown): FetchBaseQueryError {
    return {
        status: "CUSTOM_ERROR",
        error: error instanceof Error ? error.message : String(error)
    };
}

function buildWrappedSolAccount(lamports: number, owner: PublicKey): UserTokenAccount {
    const amountString = lamports.toString();
    const decimals = 9;

    return {
        address: owner.toBase58(),
        mint: WRAPPED_SOL_MINT,
        tokenAmount: {
            amount: amountString,
            decimals,
            uiAmount: lamportsToUiAmount(lamports, decimals)
        }
    };
}

function mapParsedTokenAccount(
    accountInfo: AccountInfo<ParsedAccountData | Buffer>,
    pubkey: PublicKey
): UserTokenAccount | null {
    const data = accountInfo.data;
    if (!("parsed" in data)) return null;

    const parsedData = data as ParsedAccountData;
    const info = parsedData.parsed?.info as
        | {
              mint: string;
              tokenAmount: TokenAmount & {
                  uiAmount?: number | null;
                  amount?: string;
                  decimals?: number;
              };
          }
        | undefined;

    if (!info?.mint || !info.tokenAmount) return null;
    if (info.mint === WRAPPED_SOL_MINT) return null;

    const tokenAmountInfo = info.tokenAmount;

    const decimals = tokenAmountInfo.decimals ?? 0;
    const amount = tokenAmountInfo.amount ?? "0";
    const owner = accountInfo.owner;
    const programId = owner instanceof PublicKey ? owner.toBase58() : typeof owner === "string" ? owner : null;

    if (!programId) return null;

    const uiAmount = getTokenUiAmountWithFallback(amount, decimals, tokenAmountInfo.uiAmount);

    return {
        address: pubkey.toBase58(),
        mint: info.mint,
        tokenAmount: {
            amount,
            decimals,
            uiAmount
        }
    };
}

/**
 * Verifies if a given mint is a valid Seeker Genesis Token by checking:
 * 1. Mint authority matches SGT_MINT_AUTHORITY
 * 2. Metadata pointer authority and address match SGT constants
 * 3. Token group member group matches SGT_GROUP_MINT_ADDRESS
 */
async function verifySeekerGenesisToken(connection: Connection, mintAddress: string): Promise<boolean> {
    const mintPubkey = new PublicKey(mintAddress);

    // Fetch mint account with parsed data
    const response = await axios.post(
        connection.rpcEndpoint,
        {
            jsonrpc: "2.0",
            id: 1,
            method: "getAccountInfo",
            params: [mintPubkey.toBase58(), { encoding: "jsonParsed", commitment: "confirmed" }]
        },
        {
            headers: { "Content-Type": "application/json" }
        }
    );

    const accountInfo = response.data?.result?.value;
    if (!accountInfo) {
        return false;
    }

    const parsedData = accountInfo.data?.parsed;
    if (!parsedData || parsedData.type !== "mint") {
        return false;
    }

    const mintInfo = parsedData.info as ParsedMintInfo;

    // Check mint authority
    if (mintInfo.mintAuthority !== SGT_MINT_AUTHORITY) {
        return false;
    }

    // Check extensions for metadata pointer and token group member
    const extensions = mintInfo.extensions;
    if (!extensions || !Array.isArray(extensions)) {
        return false;
    }

    let hasCorrectMetadataPointer = false;
    let hasCorrectGroupMember = false;

    for (const ext of extensions) {
        // Check metadata pointer extension
        if (ext.extension === "metadataPointer" && ext.state) {
            hasCorrectMetadataPointer =
                ext.state.authority === SGT_MINT_AUTHORITY && ext.state.metadataAddress === SGT_METADATA_ADDRESS;
        }

        // Check token group member extension
        if (ext.extension === "tokenGroupMember" && ext.state) {
            hasCorrectGroupMember = ext.state.group === SGT_GROUP_MINT_ADDRESS;
        }
    }

    return hasCorrectMetadataPointer && hasCorrectGroupMember;
}

async function fetchTokenAccounts(
    connection: Connection,
    ownerPublicKey: PublicKey
): Promise<UserTokenAccount[] | null> {
    const programIds = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
    let hasSuccessfulRpcFetch = false;

    const accountEntries: { account: AccountInfo<ParsedAccountData>; pubkey: PublicKey }[] = [];

    for (const programId of programIds) {
        try {
            const response = await axios.post(
                connection.rpcEndpoint,
                {
                    jsonrpc: "2.0",
                    id: 1,
                    method: "getTokenAccountsByOwner",
                    params: [
                        ownerPublicKey.toBase58(),
                        { programId: programId.toBase58() },
                        { encoding: "jsonParsed", commitment: "processed" }
                    ]
                },
                {
                    headers: { "Content-Type": "application/json" }
                }
            );

            hasSuccessfulRpcFetch = true;
            accountEntries.push(
                ...response.data.result.value.map(
                    ({ account, pubkey }: { account: AccountInfo<ParsedAccountData>; pubkey: string }) => ({
                        account: account as AccountInfo<ParsedAccountData>,
                        pubkey: new PublicKey(pubkey)
                    })
                )
            );
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch token accounts for program", programId.toBase58(), error);
            throw error;
        }
    }

    if (!hasSuccessfulRpcFetch) {
        return null;
    }

    let data = accountEntries
        .map(({ account, pubkey }) => mapParsedTokenAccount(account, pubkey))
        .filter((token): token is UserTokenAccount => filterTruthy(token));

    try {
        const lamports = await connection.getBalance(ownerPublicKey, "processed");
        const wrappedSolAccount = buildWrappedSolAccount(lamports, ownerPublicKey);
        data = [...data.filter((token) => token.mint !== WRAPPED_SOL_MINT), wrappedSolAccount];
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch SOL balance", error);
    }

    return data;
}

export const rpcApi = createApi({
    reducerPath: "rpcApi",
    baseQuery: fetchBaseQuery(),
    endpoints: (builder) => ({
        verifySeekerGenesisToken: builder.query<
            SGTVerificationResult,
            { mintAddress: string; tokenAccountAddress: string }
        >({
            queryFn: async ({ mintAddress, tokenAccountAddress }) => {
                try {
                    const connection = createRpcConnection();

                    // Verify the mint is a valid SGT
                    const isValidSGT = await verifySeekerGenesisToken(connection, mintAddress);

                    return {
                        data: {
                            isValid: isValidSGT,
                            mintAddress,
                            tokenAccountAddress,
                            error: isValidSGT ? undefined : "Token is not a valid Seeker Genesis Token"
                        }
                    };
                } catch (error) {
                    return { error: createQueryError(error) };
                }
            }
        }),
        tokenAccountsByOwner: builder.query<UserTokenAccount[], string>({
            queryFn: async (walletAddress, { dispatch }) => {
                try {
                    const connection = createRpcConnection();
                    const ownerPublicKey = new PublicKey(walletAddress);
                    const rpcData = await fetchTokenAccounts(connection, ownerPublicKey);

                    if (rpcData) {
                        return { data: rpcData };
                    } else {
                        throw new Error("No data returned from RPC");
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to fetch token accounts from RPC", error);
                }

                const fallbackQuery = dispatch(
                    userTokensRestApi.endpoints.userTokens.initiate(walletAddress, {
                        forceRefetch: true
                    })
                );

                try {
                    const fallbackData = await fallbackQuery.unwrap();
                    return { data: fallbackData ?? [] };
                } catch (fallbackError) {
                    return { error: createQueryError(fallbackError) };
                } finally {
                    fallbackQuery.unsubscribe?.();
                }
            },
            async onCacheEntryAdded(walletAddress, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
                try {
                    await cacheDataLoaded;
                } catch {
                    // ensure we still clean up listeners if the initial load fails
                }

                const connection = createRpcConnection();
                const ownerPublicKey = new PublicKey(walletAddress);
                const pendingParsedAccounts = new Set<string>();
                let isActive = true;
                const cleanupRpcWebsocketReconnect = attachRpcWebsocketReconnect(connection, {
                    onReconnect: async () => {
                        const refreshedAccounts = await fetchTokenAccounts(connection, ownerPublicKey);
                        if (!isActive || !refreshedAccounts) return;

                        updateCachedData((draft) => {
                            draft.splice(0, draft.length, ...refreshedAccounts);
                        });
                    }
                });

                const loadParsedTokenAccount = async (accountId: PublicKey) => {
                    const address = accountId.toBase58();
                    if (pendingParsedAccounts.has(address)) return;
                    pendingParsedAccounts.add(address);

                    try {
                        const response = await connection.getParsedAccountInfo(accountId, "processed");
                        if (!isActive) return;

                        const value = response.value as AccountInfo<ParsedAccountData | Buffer> | null;
                        const mappedAccount = value ? mapParsedTokenAccount(value, accountId) : null;

                        updateCachedData((draft) => {
                            const existingIndex = draft.findIndex((token) => token.address === address);

                            if (!mappedAccount) {
                                if (existingIndex !== -1) {
                                    draft.splice(existingIndex, 1);
                                }
                                return;
                            }

                            if (existingIndex === -1) {
                                draft.push(mappedAccount);
                            } else {
                                draft[existingIndex] = mappedAccount;
                            }
                        });
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error("Failed to load parsed token account", error);
                    } finally {
                        pendingParsedAccounts.delete(address);
                    }
                };

                const updateWrappedSol = (lamports: number) => {
                    if (!isActive) return;

                    const wrappedSolAccount = buildWrappedSolAccount(lamports, ownerPublicKey);
                    updateCachedData((draft) => {
                        const existingIndex = draft.findIndex((token) => token.mint === WRAPPED_SOL_MINT);
                        if (existingIndex === -1) {
                            draft.push(wrappedSolAccount);
                        } else {
                            draft[existingIndex] = wrappedSolAccount;
                        }
                    });
                };

                let solSubscriptionId: number | undefined;
                try {
                    solSubscriptionId = connection.onAccountChange(
                        ownerPublicKey,
                        (accountInfo) => {
                            if (!isActive) return;
                            updateWrappedSol(accountInfo.lamports);
                        },
                        "processed"
                    );
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to subscribe to SOL balance changes", error);
                }

                const filters: GetProgramAccountsFilter[] = [
                    {
                        memcmp: {
                            offset: 32,
                            bytes: walletAddress
                        }
                    }
                ];

                const programIds = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
                const programSubscriptionIds: number[] = [];

                const handleProgramAccountChange = ({ accountId, accountInfo }: KeyedAccountInfo) => {
                    if (!isActive) return;

                    const address = accountId.toBase58();
                    const accountData = accountInfo.data;

                    if (!(accountData instanceof Uint8Array)) {
                        return;
                    }

                    if (accountData.length === 0) {
                        updateCachedData((draft) => {
                            const index = draft.findIndex((token) => token.address === address);
                            if (index !== -1) {
                                draft.splice(index, 1);
                            }
                        });
                        return;
                    }

                    let shouldLoadParsedAccount = false;

                    updateCachedData((draft) => {
                        const existingIndex = draft.findIndex((token) => token.address === address);
                        const existingAccount = existingIndex === -1 ? undefined : draft[existingIndex];

                        try {
                            const decoded = AccountLayout.decode(accountData);
                            const mint = decoded.mint.toBase58();
                            const amount = decoded.amount.toString();
                            const decimals = existingAccount?.tokenAmount?.decimals;

                            if (decimals === undefined) {
                                shouldLoadParsedAccount = true;
                                return;
                            }

                            const owner = accountInfo.owner;
                            const hasValidOwner = owner instanceof PublicKey || typeof owner === "string";

                            if (!hasValidOwner) {
                                return;
                            }

                            const uiAmount = getTokenUiAmountWithFallback(
                                amount,
                                decimals,
                                existingAccount?.tokenAmount?.uiAmount
                            );

                            const updatedToken: UserTokenAccount = {
                                address,
                                mint,
                                tokenAmount: {
                                    amount,
                                    decimals,
                                    uiAmount
                                }
                            };

                            if (existingIndex === -1) {
                                draft.push(updatedToken);
                            } else {
                                draft[existingIndex] = updatedToken;
                            }
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error("Failed to decode token account from buffer", error);
                        }
                    });

                    if (shouldLoadParsedAccount) {
                        void loadParsedTokenAccount(accountId);
                    }
                };

                for (const programId of programIds) {
                    try {
                        const subscriptionId = connection.onProgramAccountChange(
                            programId,
                            handleProgramAccountChange,
                            {
                                commitment: "processed",
                                filters
                            }
                        );
                        programSubscriptionIds.push(subscriptionId);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error("Failed to subscribe to token account changes", error);
                    }
                }

                try {
                    await cacheEntryRemoved;
                } finally {
                    isActive = false;
                    cleanupRpcWebsocketReconnect();

                    if (solSubscriptionId !== undefined) {
                        try {
                            await connection.removeAccountChangeListener(solSubscriptionId);
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error("Failed to remove SOL balance subscription", error);
                        }
                    }

                    await Promise.all(
                        programSubscriptionIds.map(async (subscriptionId) => {
                            try {
                                await connection.removeProgramAccountChangeListener(subscriptionId);
                            } catch (error) {
                                // eslint-disable-next-line no-console
                                console.error("Failed to remove token account subscription", error);
                            }
                        })
                    );
                }
            }
        })
    })
});

export const { useTokenAccountsByOwnerQuery, useLazyTokenAccountsByOwnerQuery, useLazyVerifySeekerGenesisTokenQuery } =
    rpcApi;
